'use client';

import { useState, useTransition } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  precheckLogin,
  enrollConfirm,
  finishLogin,
  type PrecheckResult,
} from './actions';

type Step = 'password' | 'enroll' | 'totp';

type Enroll = { secret: string; otpauthUrl: string; qrDataUrl: string };

export function LoginForm() {
  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [enroll, setEnroll] = useState<Enroll | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, start] = useTransition();

  function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    start(async () => {
      const res: PrecheckResult = await precheckLogin(email, password);
      if (res.status === 'invalid') {
        setError('Identifiants invalides.');
      } else if (res.status === 'totp') {
        setStep('totp');
      } else {
        setEnroll({ secret: res.secret, otpauthUrl: res.otpauthUrl, qrDataUrl: res.qrDataUrl });
        setStep('enroll');
      }
    });
  }

  function submitEnroll(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!enroll) return;
    start(async () => {
      const res = await enrollConfirm(email, password, enroll.secret, code);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setBackupCodes(res.backupCodes);
      setCode('');
    });
  }

  function submitTotp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    start(async () => {
      const res = await finishLogin(email, password, code);
      if (res?.error) setError(res.error);
      // succès → redirection serveur (NEXT_REDIRECT)
    });
  }

  // Écran codes de secours (après enrôlement réussi)
  if (backupCodes) {
    return (
      <div className="space-y-4">
        <Alert>
          <AlertDescription>
            2FA activée. Note ces <strong>codes de secours</strong> : ils
            permettent de te connecter si tu perds ton téléphone.{' '}
            <strong>Ils ne seront plus affichés.</strong>
          </AlertDescription>
        </Alert>
        <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/40 p-3 font-mono text-sm">
          {backupCodes.map((c) => (
            <span key={c} className="select-all tabular-nums">
              {c}
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="cursor-pointer"
            onClick={() => {
              navigator.clipboard?.writeText(backupCodes.join('\n')).catch(() => {});
            }}
          >
            Copier
          </Button>
          <Button
            type="button"
            className="flex-1 cursor-pointer"
            onClick={() => {
              setBackupCodes(null);
              setStep('totp');
            }}
          >
            J&apos;ai noté mes codes, continuer
          </Button>
        </div>
      </div>
    );
  }

  if (step === 'enroll' && enroll) {
    return (
      <form onSubmit={submitEnroll} className="space-y-4">
        <p className="text-sm text-muted-foreground">
          La double authentification est obligatoire. Scanne ce QR code avec
          Google Authenticator, Authy ou 1Password, puis saisis le code généré.
        </p>
        <div className="flex justify-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={enroll.qrDataUrl}
            alt="QR code 2FA"
            className="rounded-md border bg-white p-2"
            width={200}
            height={200}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Clé manuelle</Label>
          <code className="block break-all rounded-md border bg-muted/40 px-2 py-1.5 text-xs select-all">
            {enroll.secret}
          </code>
        </div>
        <div className="space-y-2">
          <Label htmlFor="code">Code à 6 chiffres</Label>
          <Input
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            required
            autoFocus
          />
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <Button type="submit" className="w-full cursor-pointer" disabled={pending}>
          {pending ? 'Vérification…' : 'Activer la 2FA'}
        </Button>
      </form>
    );
  }

  if (step === 'totp') {
    return (
      <form onSubmit={submitTotp} className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Saisis le code de ton application d&apos;authentification (ou un code
          de secours).
        </p>
        <div className="space-y-2">
          <Label htmlFor="code">Code de vérification</Label>
          <Input
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            required
            autoFocus
          />
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <Button type="submit" className="w-full cursor-pointer" disabled={pending}>
          {pending ? 'Connexion…' : 'Se connecter'}
        </Button>
        <button
          type="button"
          className="w-full cursor-pointer text-center text-xs text-muted-foreground hover:underline"
          onClick={() => {
            setStep('password');
            setCode('');
            setError(null);
          }}
        >
          ← Recommencer
        </button>
      </form>
    );
  }

  // step === 'password'
  return (
    <form onSubmit={submitPassword} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          autoFocus
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Mot de passe</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Button type="submit" className="w-full cursor-pointer" disabled={pending}>
        {pending ? 'Vérification…' : 'Continuer'}
      </Button>
    </form>
  );
}
